import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { usePractice } from "../context/PracticeContext";
import {
  alertChannelsApi,
  type DispatchAttempt,
  type AlertDeliveryRow,
} from "../api/alertChannels";
import { PageSkeleton } from "../components/PageSkeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Mail,
  MessageSquare,
  Send,
  X,
  Plus,
  Bell,
  Check,
  AlertCircle,
  Clock,
} from "lucide-react";
import { cn } from "../lib/utils";

const SEVERITIES = ["info", "warning", "critical"] as const;
type Severity = (typeof SEVERITIES)[number];

function statusIcon(s: string) {
  if (s === "sent") return <Check className="h-3 w-3 text-green-500" />;
  if (s === "failed") return <AlertCircle className="h-3 w-3 text-red-500" />;
  return <Clock className="h-3 w-3 text-muted-foreground" />;
}

export function AdminAlertChannels() {
  const { setBreadcrumbs } = useBreadcrumbs();
  const { selectedPracticeId } = usePractice();
  const queryClient = useQueryClient();

  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [emailDraft, setEmailDraft] = useState("");
  const [emailSeverity, setEmailSeverity] = useState<Severity>("warning");
  const [gchatUrl, setGchatUrl] = useState("");
  const [gchatSeverity, setGchatSeverity] = useState<Severity>("warning");
  const [testSeverity, setTestSeverity] = useState<Severity>("warning");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [testAttempts, setTestAttempts] = useState<DispatchAttempt[] | null>(null);

  useEffect(() => {
    setBreadcrumbs([{ label: "Administration" }, { label: "Alert Channels" }]);
  }, [setBreadcrumbs]);

  const { data: channels, isLoading } = useQuery({
    queryKey: ["alert-channels", selectedPracticeId],
    queryFn: () => alertChannelsApi.get(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  const { data: deliveries } = useQuery({
    queryKey: ["alert-deliveries", selectedPracticeId],
    queryFn: () => alertChannelsApi.deliveries(selectedPracticeId!),
    enabled: !!selectedPracticeId,
    refetchInterval: 10_000,
  });

  // Hydrate form from server response
  useEffect(() => {
    if (channels?.email) {
      setEmailRecipients(channels.email.recipients ?? []);
      if (channels.email.severityFilter) {
        setEmailSeverity(channels.email.severityFilter);
      }
    }
    if (channels?.gchat?.severityFilter) {
      setGchatSeverity(channels.gchat.severityFilter);
    }
  }, [channels]);

  const save = useMutation({
    mutationFn: async () => {
      if (!selectedPracticeId) throw new Error("No company");
      const body: Parameters<typeof alertChannelsApi.save>[1] = {
        email: { recipients: emailRecipients, severityFilter: emailSeverity },
      };
      if (gchatUrl.trim()) {
        body.gchat = {
          webhookUrl: gchatUrl.trim(),
          severityFilter: gchatSeverity,
        };
      }
      return alertChannelsApi.save(selectedPracticeId, body);
    },
    onSuccess: () => {
      setFeedback("Alert channel configuration saved.");
      setGchatUrl(""); // never echo back the webhook
      queryClient.invalidateQueries({
        queryKey: ["alert-channels", selectedPracticeId],
      });
    },
    onError: (e) => setFeedback(`Save failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  const test = useMutation({
    mutationFn: () => alertChannelsApi.test(selectedPracticeId!, testSeverity),
    onSuccess: (res) => {
      setTestAttempts(res.result.attempts);
      setFeedback(
        `Test alert dispatched (id: ${res.alertId.slice(0, 8)}). ${res.result.attempts.length} channel attempt${res.result.attempts.length === 1 ? "" : "s"}.`,
      );
      queryClient.invalidateQueries({
        queryKey: ["alert-deliveries", selectedPracticeId],
      });
    },
    onError: (e) => setFeedback(`Test failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  function addEmail() {
    const v = emailDraft.trim();
    if (!v) return;
    if (!v.includes("@")) {
      setFeedback("That doesn't look like an email address.");
      return;
    }
    if (!emailRecipients.includes(v)) {
      setEmailRecipients([...emailRecipients, v]);
    }
    setEmailDraft("");
  }

  function removeEmail(v: string) {
    setEmailRecipients(emailRecipients.filter((e) => e !== v));
  }

  if (isLoading) return <PageSkeleton variant="list" />;

  return (
    <div className="space-y-4 p-6 max-w-3xl mx-auto">
      <div>
        <h2 className="text-xl font-bold">Alert Channels</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Where to send alerts when a monitored metric breaches its threshold.
          The in-app banner is always on; configure email and Google Chat below.
        </p>
      </div>

      {feedback && (
        <div className="rounded-md border border-border bg-muted/30 px-4 py-2 text-sm">
          {feedback}
        </div>
      )}

      {/* ── Email card ────────────────────────────────────────── */}
      <div className="rounded-lg border border-border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Email (AWS SES)</h3>
        </div>
        <div className="space-y-2">
          <label className="text-xs text-muted-foreground">Recipients</label>
          <div className="flex flex-wrap gap-1.5">
            {emailRecipients.map((r) => (
              <Badge
                key={r}
                variant="secondary"
                className="gap-1 pr-1"
              >
                {r}
                <button
                  onClick={() => removeEmail(r)}
                  className="hover:text-destructive ml-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {emailRecipients.length === 0 && (
              <span className="text-xs text-muted-foreground italic">
                None — emails will not be sent.
              </span>
            )}
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="email"
              className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
              placeholder="alerts@vipmedicalgroup.com"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addEmail();
                }
              }}
            />
            <Button size="sm" variant="outline" onClick={addEmail}>
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <label className="text-xs text-muted-foreground">Send when severity is at least:</label>
          <select
            className="rounded-md border border-input bg-background px-2 py-1 text-sm"
            value={emailSeverity}
            onChange={(e) => setEmailSeverity(e.target.value as Severity)}
          >
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Google Chat card ──────────────────────────────────── */}
      <div className="rounded-lg border border-border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Google Chat (Incoming Webhook)</h3>
        </div>
        <div className="space-y-2">
          <label className="text-xs text-muted-foreground">
            Webhook URL{" "}
            {channels?.gchat?.webhookConfigured && (
              <span className="text-green-600">— currently configured (paste again to update)</span>
            )}
          </label>
          <input
            type="password"
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm font-mono focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
            placeholder="https://chat.googleapis.com/v1/spaces/.../messages?key=...&token=..."
            value={gchatUrl}
            onChange={(e) => setGchatUrl(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2 text-sm">
          <label className="text-xs text-muted-foreground">Send when severity is at least:</label>
          <select
            className="rounded-md border border-input bg-background px-2 py-1 text-sm"
            value={gchatSeverity}
            onChange={(e) => setGchatSeverity(e.target.value as Severity)}
          >
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Save + Test row ───────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3">
        <Button
          onClick={() => save.mutate()}
          disabled={save.isPending || !selectedPracticeId}
        >
          {save.isPending ? "Saving…" : "Save channels"}
        </Button>
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground">Test as:</label>
          <select
            className="rounded-md border border-input bg-background px-2 py-1 text-sm"
            value={testSeverity}
            onChange={(e) => setTestSeverity(e.target.value as Severity)}
          >
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            variant="outline"
            onClick={() => test.mutate()}
            disabled={test.isPending || !selectedPracticeId}
          >
            <Send className="h-3.5 w-3.5 mr-1.5" />
            {test.isPending ? "Sending…" : "Send test alert"}
          </Button>
        </div>
      </div>

      {/* ── Test result inline ────────────────────────────────── */}
      {testAttempts && (
        <div className="rounded-lg border border-border bg-card p-4 space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Last test result
          </h4>
          <div className="space-y-1">
            {testAttempts.map((a, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                {statusIcon(a.status)}
                <span className="font-mono text-muted-foreground">{a.channel}</span>
                <span className={cn("font-medium", a.status === "failed" ? "text-red-600" : a.status === "skipped" ? "text-muted-foreground" : "text-foreground")}>
                  {a.status}
                </span>
                {a.recipient && <span className="text-muted-foreground">→ {a.recipient}</span>}
                {a.errorMessage && (
                  <span className="text-muted-foreground italic">— {a.errorMessage}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Audit log ─────────────────────────────────────────── */}
      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <Bell className="h-3.5 w-3.5 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Recent delivery attempts</h3>
          <span className="text-xs text-muted-foreground">
            {deliveries?.length ?? 0}
          </span>
        </div>
        <div className="divide-y divide-border max-h-96 overflow-auto">
          {(deliveries ?? []).slice(0, 50).map((d: AlertDeliveryRow) => (
            <div
              key={d.id}
              className="flex items-center gap-2 px-4 py-2 text-xs"
            >
              {statusIcon(d.status)}
              <span className="font-mono w-12">{d.channel}</span>
              <span className="text-muted-foreground w-12">{d.status}</span>
              {d.recipient && (
                <span className="text-muted-foreground truncate flex-1">
                  → {d.recipient}
                </span>
              )}
              {d.errorMessage && (
                <span className="text-red-600 italic truncate flex-1">
                  {d.errorMessage}
                </span>
              )}
              <span className="text-muted-foreground ml-auto whitespace-nowrap">
                {new Date(d.attemptedAt).toLocaleString()}
              </span>
            </div>
          ))}
          {(!deliveries || deliveries.length === 0) && (
            <div className="px-4 py-6 text-xs text-muted-foreground text-center">
              No delivery attempts yet. Configure channels above and click "Send test alert" to verify.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
